import React, { useState } from 'react';
import { Upload, Search, Trash2, CheckCircle, AlertCircle, Loader2, Database, FileText, BarChart3 } from 'lucide-react';
import { importGMCTData } from '../../scripts/importGMCTData';
import { ragService } from '../../services/rag.service';

interface ImportProgress {
  processed: number;
  total: number;
  status: string;
}

interface SearchResult {
  id: string;
  content: string;
  metadata?: {
    category?: string;
    type?: string;
    source?: string;
  };
  similarity_score?: number;
}

interface Alert {
  type: 'success' | 'error' | 'info';
  message: string;
  details?: string;
}

const TestImport: React.FC = () => {
  // Import state
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress>({ processed: 0, total: 0, status: '' });
  const [importResult, setImportResult] = useState<any>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);

  // Clear data state
  const [isClearing, setIsClearing] = useState(false);

  // Stats state
  const [stats, setStats] = useState<any>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Alert state
  const [alert, setAlert] = useState<Alert | null>(null);

  const showAlert = (type: Alert['type'], message: string, details?: string) => {
    setAlert({ type, message, details });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleImport = async () => {
    try {
      setIsImporting(true);
      setImportResult(null);
      setImportProgress({ processed: 0, total: 0, status: 'Starting import...' });

      const result = await importGMCTData('gmct', (processed, total, status) => {
        setImportProgress({ processed, total, status });
      });

      setImportResult(result);

      if (result.success) {
        showAlert('success', 'Import completed successfully!', 
          `Processed ${result.processed} chunks, ${result.failed} failed`);
        // Refresh stats after successful import
        loadStats();
      } else {
        showAlert('error', 'Import failed', result.error || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Import error:', error);
      showAlert('error', 'Import failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsImporting(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      showAlert('error', 'Please enter a search query');
      return;
    }

    try {
      setIsSearching(true);
      setSearchResults([]);

      const response = await ragService.queryKnowledgeBase({
        query: searchQuery,
        cemetery_id: 'gmct',
        context_limit: 3000,
        similarity_threshold: 0.7
      });

      if (response.error) {
        showAlert('error', 'Search failed', response.error);
        return;
      }

      // For demo purposes, also get some sample chunks to show
      const chunks = await ragService.getKnowledgeChunks('gmct');
      const filteredChunks = chunks
        .filter(chunk => 
          chunk.content.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .slice(0, 5)
        .map(chunk => ({
          id: chunk.id,
          content: chunk.content,
          metadata: chunk.metadata,
          similarity_score: Math.random() * 0.3 + 0.7 // Mock similarity score
        }));

      setSearchResults(filteredChunks);
      
      if (filteredChunks.length === 0) {
        showAlert('info', 'No results found', 'Try a different search term');
      } else {
        showAlert('success', `Found ${filteredChunks.length} results`);
      }
    } catch (error) {
      console.error('Search error:', error);
      showAlert('error', 'Search failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearData = async () => {
    if (!confirm('Are you sure you want to delete all GMCT knowledge chunks? This action cannot be undone.')) {
      return;
    }

    try {
      setIsClearing(true);

      const success = await ragService.clearKnowledgeBase('gmct');

      if (success) {
        showAlert('success', 'Knowledge base cleared successfully');
        setSearchResults([]);
        setImportResult(null);
        loadStats();
      } else {
        showAlert('error', 'Failed to clear knowledge base');
      }
    } catch (error) {
      console.error('Clear error:', error);
      showAlert('error', 'Clear operation failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsClearing(false);
    }
  };

  const loadStats = async () => {
    try {
      setIsLoadingStats(true);
      const knowledgeStats = await ragService.getKnowledgeBaseStats('gmct');
      setStats(knowledgeStats);
    } catch (error) {
      console.error('Stats loading error:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Load stats on component mount
  React.useEffect(() => {
    loadStats();
  }, []);

  const getProgressPercentage = () => {
    if (importProgress.total === 0) return 0;
    return Math.round((importProgress.processed / importProgress.total) * 100);
  };

  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'general': return 'bg-blue-100 text-blue-700';
      case 'location': return 'bg-green-100 text-green-700';
      case 'services': return 'bg-purple-100 text-purple-700';
      case 'policy': return 'bg-orange-100 text-orange-700';
      case 'pricing': return 'bg-yellow-100 text-yellow-700';
      case 'contact': return 'bg-red-100 text-red-700';
      case 'faq': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      <div className="px-4 py-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Knowledge Base Management
            </h1>
            <p className="text-gray-600">
              Import, test, and manage GMCT knowledge base data
            </p>
          </div>

          {/* Alert */}
          {alert && (
            <div className={`mb-6 p-4 rounded-lg border ${
              alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
              alert.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
              'bg-blue-50 border-blue-200 text-blue-800'
            }`}>
              <div className="flex items-start">
                {alert.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                ) : alert.type === 'error' ? (
                  <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <p className="font-medium">{alert.message}</p>
                  {alert.details && (
                    <p className="text-sm mt-1 opacity-80">{alert.details}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Stats Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                Knowledge Base Statistics
              </h2>
              <button
                onClick={loadStats}
                disabled={isLoadingStats}
                className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-sm hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                {isLoadingStats ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Refresh'
                )}
              </button>
            </div>

            {stats ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-900">{stats.total_chunks}</div>
                  <div className="text-sm text-blue-700">Total Chunks</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-900">{stats.chunks_with_embeddings}</div>
                  <div className="text-sm text-green-700">With Embeddings</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-purple-900">
                    {Object.keys(stats.categories).length}
                  </div>
                  <div className="text-sm text-purple-700">Categories</div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {isLoadingStats ? 'Loading statistics...' : 'No statistics available'}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Import Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Upload className="w-5 h-5 mr-2 text-green-600" />
                Import Data
              </h2>

              <div className="space-y-4">
                <p className="text-gray-600 text-sm">
                  Import GMCT sample data including cemetery information, services, policies, and FAQs.
                </p>

                <button
                  onClick={handleImport}
                  disabled={isImporting}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {isImporting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Database className="w-5 h-5 mr-2" />
                      Import GMCT Sample Data
                    </>
                  )}
                </button>

                {/* Progress Bar */}
                {isImporting && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Progress</span>
                      <span>{getProgressPercentage()}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${getProgressPercentage()}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-600">{importProgress.status}</p>
                  </div>
                )}

                {/* Import Results */}
                {importResult && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Import Results</h3>
                    <div className="text-sm space-y-1">
                      <p><span className="font-medium">Total Chunks:</span> {importResult.total_chunks}</p>
                      <p><span className="font-medium">Processed:</span> {importResult.processed}</p>
                      <p><span className="font-medium">Failed:</span> {importResult.failed}</p>
                      {importResult.errors && importResult.errors.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-red-600">
                            View Errors ({importResult.errors.length})
                          </summary>
                          <div className="mt-2 text-xs bg-red-50 p-2 rounded max-h-32 overflow-y-auto">
                            {importResult.errors.map((error: string, index: number) => (
                              <div key={index} className="text-red-700">{error}</div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Test Search Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Search className="w-5 h-5 mr-2 text-blue-600" />
                Test Search
              </h2>

              <div className="space-y-4">
                <p className="text-gray-600 text-sm">
                  Test the RAG search functionality with sample queries.
                </p>

                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="e.g., visiting hours, burial costs, contact information"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleSearch}
                    disabled={isSearching || !searchQuery.trim()}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSearching ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Search className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {/* Quick Search Buttons */}
                <div className="flex flex-wrap gap-2">
                  {['visiting hours', 'burial costs', 'contact phone', 'emergency number'].map((query) => (
                    <button
                      key={query}
                      onClick={() => {
                        setSearchQuery(query);
                        setTimeout(() => handleSearch(), 100);
                      }}
                      className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full hover:bg-gray-200 transition-colors"
                    >
                      {query}
                    </button>
                  ))}
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    <h3 className="font-medium text-gray-900">
                      Search Results ({searchResults.length})
                    </h3>
                    {searchResults.map((result) => (
                      <div key={result.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            {result.metadata?.category && (
                              <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(result.metadata.category)}`}>
                                {result.metadata.category}
                              </span>
                            )}
                          </div>
                          {result.similarity_score && (
                            <span className="text-xs text-gray-500">
                              {Math.round(result.similarity_score * 100)}% match
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {result.content.length > 200 
                            ? `${result.content.substring(0, 200)}...` 
                            : result.content
                          }
                        </p>
                        {result.metadata?.type && (
                          <p className="text-xs text-gray-500 mt-2">
                            Type: {result.metadata.type} | Source: {result.metadata.source || 'unknown'}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Clear Data Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Trash2 className="w-5 h-5 mr-2 text-red-600" />
              Clear Data
            </h2>

            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-red-800 mb-1">Danger Zone</h3>
                    <p className="text-sm text-red-700">
                      This will permanently delete all GMCT knowledge chunks from the database. 
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleClearData}
                disabled={isClearing}
                className="bg-red-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isClearing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Clearing...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5 mr-2" />
                    Clear All GMCT Data
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Development Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-8">
            <h3 className="font-medium text-blue-800 mb-2">Development Notes</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Import processes ~25 knowledge chunks with embeddings</li>
              <li>• Search uses text-based matching (semantic search via API)</li>
              <li>• All operations are scoped to GMCT cemetery (cemetery_id: 'gmct')</li>
              <li>• Embeddings are generated using OpenAI text-embedding-3-small</li>
              <li>• Data includes general info, locations, services, policies, and FAQs</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestImport;